// components/sections/Contact.js
import React, { useState } from 'react';
import styled from 'styled-components';
import Container from '../common/Container';
import { SectionTitle, SectionSubtitle } from '../common/SharedStyles';
import Button from '../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarker,
    faPhone,
    faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { sendContactMessage } from '../../services/contactService';

const ContactWrapper = styled.section`
  background: var(--background-secondary);
  padding: 6rem 0;
  width: 100%;

  @media (max-width: 576px) {
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  .icon {
    color: var(--accent-primary);
    font-size: 1.5rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--background-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--background-primary);
  color: var(--text-primary);
  min-height: 150px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
`;

const FormStatus = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.875rem;

  &.error {
    background-color: rgba(229, 57, 53, 0.1);
    color: #e53935;

    @media (prefers-color-scheme: dark) {
      background-color: rgba(229, 57, 53, 0.2);
      color: #ff6b6b;
    }
  }

  &.success {
    background-color: rgba(67, 160, 71, 0.1);
    color: #43a047;

    @media (prefers-color-scheme: dark) {
      background-color: rgba(67, 160, 71, 0.2);
      color: #81c784;
    }
  }

  &.sending {
    background-color: rgba(3, 169, 244, 0.1);
    color: #0288d1;

    @media (prefers-color-scheme: dark) {
      background-color: rgba(3, 169, 244, 0.2);
      color: #4fc3f7;
    }
  }
`;

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [status, setStatus] = useState('');
    const [statusClass, setStatusClass] = useState('');
    const [isSending, setIsSending] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            setStatus('Please fill in all fields');
            setStatusClass('error');
            return;
        }

        setIsSending(true);
        setStatus('Sending message...');
        setStatusClass('sending');

        try {
            await sendContactMessage(formData);
            setStatus('Message sent successfully! We\'ll get back to you soon.');
            setStatusClass('success');

            // Clear form
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: ''
            });
        } catch (error) {
            setStatus(`Error: ${error.message}`);
            setStatusClass('error');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <ContactWrapper id="contact">
            <Container>
                <SectionTitle>Get in Touch</SectionTitle>
                <SectionSubtitle>
                    Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                </SectionSubtitle>

                <ContactGrid>
                    <ContactInfo>
                        <InfoItem>
                            <FontAwesomeIcon icon={faEnvelope} className="icon" />
                            <div>
                                <h3>Email Us</h3>
                                <p>support@signage-llc.com</p>
                            </div>
                        </InfoItem>
                        <InfoItem>
                            <FontAwesomeIcon icon={faInstagram} className="icon" />
                            <div>
                                <h3>Follow Us on Social Media</h3>
                                <p>For a chance to receive $50 off your subscription — one winner is selected each month!</p>
                                <p><a href="https://instagram.com/signagellc" target="_blank" rel="noopener noreferrer">@signagellc</a></p>
                            </div>
                        </InfoItem>
                    </ContactInfo>

                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <label>Name</label>
                            <Input
                                type="text"
                                name="name"
                                placeholder="Your name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Email</label>
                            <Input
                                type="email"
                                name="email"
                                placeholder="your@email.com"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Subject</label>
                            <Input
                                type="text"
                                name="subject"
                                placeholder="How can we help?"
                                value={formData.subject}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Message</label>
                            <Textarea
                                name="message"
                                placeholder="Your message..."
                                value={formData.message}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        {status && (
                            <FormStatus className={statusClass}>
                                {status}
                            </FormStatus>
                        )}

                        <Button variant="primary" type="submit" disabled={isSending}>
                            {isSending ? 'Sending...' : 'Send Message'}
                        </Button>
                    </Form>
                </ContactGrid>
            </Container>
        </ContactWrapper>
    );
};

export default Contact;
